import React, { useCallback, useEffect, useRef, useState } from "react";
import { Badge, Button, ButtonGroup, Form, Modal, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import { getMyFlagReminders } from "../../services/leadTrackerApi";

// Lead flags: several employees can flag a lead, each with a personal follow-up reminder. These helpers
// and components are shared by the lead team portal and the admin lead tracker.

const pad2 = (value) => String(value).padStart(2, "0");
const startOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();

export function toReminderInput(value) {
  if (!value) return "";
  if (value instanceof Date) {
    return `${value.getFullYear()}-${pad2(value.getMonth() + 1)}-${pad2(value.getDate())}T${pad2(value.getHours())}:${pad2(value.getMinutes())}`;
  }
  return String(value).slice(0, 16);
}

export function myLeadFlag(lead, memberId) {
  return (lead?.flags || []).find((flag) => flag.memberId === memberId) || null;
}

/** Worked out on the client too, since a page left open goes stale: OVERDUE, TODAY, UPCOMING or NONE. */
export function flagReminderStatus(remindAt, now = new Date()) {
  if (!remindAt) return "NONE";
  const at = new Date(remindAt);
  if (Number.isNaN(at.getTime())) return "NONE";
  if (at <= now) return "OVERDUE";
  return startOfDay(at) === startOfDay(now) ? "TODAY" : "UPCOMING";
}

export function formatFlagReminder(remindAt, now = new Date()) {
  if (!remindAt) return "No reminder";
  const at = new Date(remindAt);
  if (Number.isNaN(at.getTime())) return "-";
  const time = at.toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit", hour12: true });
  const dayDiff = Math.round((startOfDay(at) - startOfDay(now)) / 86_400_000);
  const day = dayDiff === 0 ? "Today"
    : dayDiff === 1 ? "Tomorrow"
      : dayDiff === -1 ? "Yesterday"
        : at.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
  return `${day} ${time}`;
}

const STATUS_VARIANT = { OVERDUE: "danger", TODAY: "warning", UPCOMING: "secondary", NONE: "light" };

export function FlagReminderBadge({ remindAt, className = "" }) {
  if (!remindAt) return null;
  const status = flagReminderStatus(remindAt);
  return <Badge bg={STATUS_VARIANT[status]} text={status === "TODAY" ? "dark" : undefined} className={className}
    title={status === "OVERDUE" ? "Follow-up reminder has passed" : "Your follow-up reminder"}>
    <i className="bi bi-alarm me-1" />{status === "OVERDUE" ? "Overdue · " : ""}{formatFlagReminder(remindAt)}
  </Badge>;
}

/** "Siva (Today 4:30 PM): price check" lines, for tooltips. */
export function flagSummaryText(flags) {
  return (flags || []).map((flag) => `${flag.memberName}${flag.remindAt ? ` (${formatFlagReminder(flag.remindAt)})` : ""}${flag.note ? `: ${flag.note}` : ""}`).join("\n");
}

// ---- My Flagged views --------------------------------------------------------------------------

export const isFlagFilter = (filter) => /^(my|team)Flagged(:|$)/.test(filter || "");

const BUCKETS = [
  ["", "All", "total"],
  ["OVERDUE", "Overdue", "overdue"],
  ["TODAY", "Due today", "today"],
  ["UPCOMING", "Upcoming", "upcoming"],
  ["NONE", "No reminder", "noReminder"],
];

export function flagFilterLabel(filter) {
  if (!isFlagFilter(filter)) return null;
  const [scope, bucket = ""] = filter.split(":");
  const scopeLabel = scope === "teamFlagged" ? "Team Flagged" : "My Flagged";
  const bucketLabel = BUCKETS.find(([key]) => key === bucket)?.[1];
  return bucket && bucketLabel ? `${scopeLabel} · ${bucketLabel}` : scopeLabel;
}

/** One-line breakdown shown inside the My Flagged card. */
export function FlagCountsLine({ counts, prefix = "" }) {
  if (!counts || !counts.total) return null;
  return <div className="small mt-1">
    {prefix}
    {counts.overdue > 0 && <span className="text-danger fw-semibold">{counts.overdue} overdue</span>}
    {counts.overdue > 0 && counts.today > 0 && " · "}
    {counts.today > 0 && <span className="fw-semibold">{counts.today} today</span>}
    {!counts.overdue && !counts.today && <span className="text-muted">nothing due</span>}
  </div>;
}

/** Scope (Mine / Team for managers) and reminder-bucket tabs above the list while a flag view is open. */
export function FlagViewBar({ filter, summary, isManager, onFilter }) {
  if (!isFlagFilter(filter)) return null;
  const [scope, bucket = ""] = filter.split(":");
  const counts = (scope === "teamFlagged" ? summary?.teamFlags : summary?.myFlags) || {};
  const go = (nextScope, nextBucket) => onFilter(nextBucket ? `${nextScope}:${nextBucket}` : nextScope);
  return <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
    {isManager && <ButtonGroup size="sm">
      <Button variant={scope === "myFlagged" ? "primary" : "outline-primary"} onClick={() => go("myFlagged", bucket)}>Mine</Button>
      <Button variant={scope === "teamFlagged" ? "primary" : "outline-primary"} onClick={() => go("teamFlagged", bucket)}>Team</Button>
    </ButtonGroup>}
    <ButtonGroup size="sm">
      {BUCKETS.map(([key, label, countKey]) => {
        const active = bucket === key;
        const variant = key === "OVERDUE" ? "danger" : key === "TODAY" ? "warning" : "secondary";
        return <Button key={key || "all"} variant={active ? variant : `outline-${variant}`} onClick={() => go(scope, key)}>
          {label} <Badge bg={active ? "light" : variant} text={active || key === "TODAY" ? "dark" : undefined}>{Number(counts[countKey] || 0)}</Badge>
        </Button>;
      })}
    </ButtonGroup>
    <span className="small text-muted">Sorted by follow-up time</span>
  </div>;
}

// ---- Flag dialog -------------------------------------------------------------------------------

function reminderPresets(now = new Date()) {
  const at = (days, hours, minutes = 0) => {
    const date = new Date(now.getFullYear(), now.getMonth(), now.getDate() + days, hours, minutes);
    return date;
  };
  const inMinutes = (minutes) => new Date(Math.ceil((now.getTime() + minutes * 60_000) / 300_000) * 300_000);
  const presets = [["In 1 hour", inMinutes(60)], ["In 3 hours", inMinutes(180)]];
  if (now.getHours() < 18) presets.push(["Today 6 PM", at(0, 18)]);
  presets.push(["Tomorrow 10 AM", at(1, 10)], ["Tomorrow 4 PM", at(1, 16)]);
  return presets;
}

/** Add, edit or remove your own flag on a lead; shows everyone else's flags for context. */
export function LeadFlagModal({ lead, memberId, saving, onSave, onRemove, onHide }) {
  const mine = myLeadFlag(lead, memberId);
  const [note, setNote] = useState("");
  const [remindAt, setRemindAt] = useState("");
  useEffect(() => {
    setNote(mine?.note || "");
    setRemindAt(toReminderInput(mine?.remindAt));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lead?.id]);
  if (!lead) return null;
  const others = (lead.flags || []).filter((flag) => flag.memberId !== memberId);
  const pastReminder = remindAt && new Date(remindAt) <= new Date();
  return <Modal show onHide={saving ? undefined : onHide} centered>
    <Modal.Header closeButton={!saving}>
      <Modal.Title><i className="bi bi-flag-fill text-danger me-2" />{mine ? "Your flag" : "Flag lead"} · {lead.leadId}</Modal.Title>
    </Modal.Header>
    <Modal.Body>
      <Form.Group className="mb-3">
        <Form.Label>Reason</Form.Label>
        <Form.Control as="textarea" rows={2} value={note} disabled={saving} placeholder="e.g. Customer asked to call back after 4 PM" onChange={(event) => setNote(event.target.value)} />
      </Form.Group>
      <Form.Group>
        <Form.Label>Remind me</Form.Label>
        <div className="d-flex flex-wrap gap-1 mb-2">
          {reminderPresets().map(([label, date]) => <Button key={label} size="sm" variant="outline-primary" disabled={saving} onClick={() => setRemindAt(toReminderInput(date))}>{label}</Button>)}
          <Button size="sm" variant="outline-secondary" disabled={saving || !remindAt} onClick={() => setRemindAt("")}>No reminder</Button>
        </div>
        <Form.Control type="datetime-local" value={remindAt} disabled={saving} onChange={(event) => setRemindAt(event.target.value)} />
        <Form.Text className={pastReminder ? "text-danger" : "text-muted"}>
          {pastReminder ? "This time has already passed - the flag will show as overdue." : "Only you see this reminder. It does not change the lead's follow-up date."}
        </Form.Text>
      </Form.Group>
      {others.length > 0 && <div className="mt-3 small">
        <div className="fw-semibold mb-1">Also flagged by</div>
        {others.map((flag) => <div key={flag.id} className="d-flex flex-wrap gap-2 align-items-center mb-1">
          <span>{flag.memberName}</span><FlagReminderBadge remindAt={flag.remindAt} />{flag.note && <span className="text-muted">{flag.note}</span>}
        </div>)}
      </div>}
    </Modal.Body>
    <Modal.Footer className="justify-content-between">
      <div>{mine && <Button variant="outline-danger" disabled={saving} onClick={onRemove}>Remove my flag</Button>}</div>
      <div className="d-flex gap-2">
        <Button variant="secondary" disabled={saving} onClick={onHide}>Cancel</Button>
        <Button disabled={saving} onClick={() => onSave(note, remindAt)}>{saving ? <><Spinner size="sm" className="me-2" />Saving</> : mine ? "Update flag" : "Flag lead"}</Button>
      </div>
    </Modal.Footer>
  </Modal>;
}

// ---- Admin view --------------------------------------------------------------------------------

/** Every employee's flags for the admin Flags tab: filter by reminder bucket and employee, overdue rows highlighted. */
export function AdminLeadFlagsTable({ rows = [], loading = false }) {
  const [bucket, setBucket] = useState("");
  const [memberId, setMemberId] = useState("");
  const now = new Date();
  const withStatus = rows.map((row) => ({ ...row, status: flagReminderStatus(row.remindAt, now) }));
  const members = [...new Map(withStatus.map((row) => [row.memberId, row.memberName])).entries()]
    .sort((left, right) => String(left[1]).localeCompare(String(right[1])));
  const byMember = memberId ? withStatus.filter((row) => String(row.memberId) === memberId) : withStatus;
  const counts = { total: byMember.length };
  byMember.forEach((row) => { counts[row.status] = (counts[row.status] || 0) + 1; });
  const visible = bucket ? byMember.filter((row) => row.status === bucket) : byMember;
  const flaggedAt = (value) => (value ? new Date(value).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit", hour12: true }) : "-");
  return <div>
    <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
      <ButtonGroup size="sm">
        {BUCKETS.map(([key, label]) => {
          const variant = key === "OVERDUE" ? "danger" : key === "TODAY" ? "warning" : "secondary";
          const count = key ? counts[key] || 0 : counts.total;
          return <Button key={key || "all"} variant={bucket === key ? variant : `outline-${variant}`} onClick={() => setBucket(key)}>{label} ({count})</Button>;
        })}
      </ButtonGroup>
      <Form.Select size="sm" style={{ width: 220 }} value={memberId} onChange={(event) => setMemberId(event.target.value)}>
        <option value="">All employees</option>
        {members.map(([id, name]) => <option key={id} value={String(id)}>{name}</option>)}
      </Form.Select>
      {loading && <Spinner size="sm" />}
    </div>
    <div className="table-responsive">
      <table className="table table-hover align-middle">
        <thead><tr><th style={{ width: 44 }}>#</th><th>Employee</th><th>Team</th><th>Lead</th><th>Customer</th><th>Destination</th><th>Lead Status</th><th>Reason</th><th>Flagged</th><th>Reminder</th></tr></thead>
        <tbody>
          {visible.map((row, index) => <tr key={row.flagId} className={row.status === "OVERDUE" ? "table-danger" : undefined}>
            <td>{index + 1}</td>
            <td className="fw-semibold">{row.memberName}</td>
            <td>{row.memberTeam || "-"}</td>
            <td>{row.leadCode}</td>
            <td>{row.customerName || "-"}<div className="small text-muted">{row.customerNumber}</div></td>
            <td>{row.destinationName || "-"}</td>
            <td>{row.interestStatus || "-"}</td>
            <td className="small">{row.note || "-"}</td>
            <td className="small">{flaggedAt(row.flaggedAt)}</td>
            <td>{row.remindAt ? <FlagReminderBadge remindAt={row.remindAt} /> : <span className="small text-muted">No reminder</span>}</td>
          </tr>)}
          {!visible.length && <tr><td colSpan={10} className="text-center text-muted py-4">{loading ? "Loading flags..." : "No flagged leads."}</td></tr>}
        </tbody>
      </table>
    </div>
  </div>;
}

// ---- Reminder pop-ups --------------------------------------------------------------------------

const shownKey = (memberId) => `leadFlagRemindersShown:${memberId}`;
const readShown = (memberId) => {
  try { return new Set(JSON.parse(localStorage.getItem(shownKey(memberId)) || "[]")); } catch { return new Set(); }
};
const writeShown = (memberId, shown) => {
  try { localStorage.setItem(shownKey(memberId), JSON.stringify([...shown].slice(-300))); } catch { /* storage unavailable */ }
};

/**
 * Pops a reminder once each flag's reminder time arrives while the portal is open, and once on login
 * for anything that came due while away. A reminder that is moved (snoozed) pops again at its new time.
 */
export function useFlagReminderPopups({ enabled, team, token, memberId, onOpen, onSnooze }) {
  const [reminders, setReminders] = useState([]);
  // The portal builds token/handlers afresh each render; a ref keeps the polling effects stable.
  const handlers = useRef({ onOpen, onSnooze, token });
  handlers.current = { onOpen, onSnooze, token };

  const refresh = useCallback(async () => {
    if (!enabled || !memberId) return;
    try {
      setReminders(await getMyFlagReminders(team, handlers.current.token()));
    } catch {
      // The next poll retries.
    }
  }, [enabled, team, memberId]);

  useEffect(() => {
    if (!enabled || !memberId) return undefined;
    void refresh();
    const poller = window.setInterval(() => { void refresh(); }, 60_000);
    return () => window.clearInterval(poller);
  }, [enabled, memberId, refresh]);

  useEffect(() => {
    if (!enabled || !memberId) return undefined;
    const check = () => {
      const shown = readShown(memberId);
      const now = new Date();
      let changed = false;
      reminders.forEach((reminder) => {
        if (!reminder.remindAt || new Date(reminder.remindAt) > now) return;
        const key = `${reminder.flagId}|${reminder.remindAt}`;
        if (shown.has(key)) return;
        shown.add(key);
        changed = true;
        toast.warning(({ closeToast }) => <div>
          <div className="fw-semibold"><i className="bi bi-alarm me-1" />Follow up: {reminder.customerName || reminder.customerNumber || reminder.leadCode}</div>
          <div className="small">{reminder.leadCode}{reminder.destinationName ? ` · ${reminder.destinationName}` : ""} · {formatFlagReminder(reminder.remindAt)}</div>
          {reminder.note && <div className="small fst-italic">{reminder.note}</div>}
          <div className="d-flex flex-wrap gap-1 mt-2">
            <Button size="sm" variant="dark" onClick={() => { handlers.current.onOpen?.(reminder); closeToast?.(); }}>Show</Button>
            <Button size="sm" variant="outline-dark" onClick={() => { handlers.current.onSnooze?.(reminder, 15); closeToast?.(); }}>Snooze 15 min</Button>
            <Button size="sm" variant="outline-dark" onClick={() => { handlers.current.onSnooze?.(reminder, 60); closeToast?.(); }}>1 hour</Button>
          </div>
        </div>, { autoClose: false, closeOnClick: false, toastId: `lead-flag-${key}` });
      });
      if (changed) writeShown(memberId, shown);
    };
    check();
    const timer = window.setInterval(check, 30_000);
    return () => window.clearInterval(timer);
  }, [enabled, memberId, reminders]);

  return { reminders, refresh };
}
